import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { WeeklyAttendanceReportCard } from '../../components/WeeklyAttendanceReportCard';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getMyWeeklyAttendance } from '../../services/attendanceService';
import type { WeeklyAttendanceEmployeeReport } from '../../types/weeklyAttendanceReport';
import { attendanceErrorMessage } from '../../utils/attendanceErrors';
import {
  formatWeekRangeLabel,
  getIsoWeekBounds,
  shiftIsoWeek,
} from '../../utils/weeklyAttendanceReport';

type Props = NativeStackScreenProps<RootStackParamList, 'MyWeeklyAttendance'>;

export function MyWeeklyAttendanceScreen({ navigation }: Props) {
  const palette = useAppColors();
  const defaults = useMemo(() => getIsoWeekBounds(), []);
  const [week, setWeek] = useState(defaults);
  const [report, setReport] = useState<WeeklyAttendanceEmployeeReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canGoBack =
    typeof navigation.canGoBack === 'function' && navigation.canGoBack();

  const load = useCallback(
    async (isRefresh = false) => {
      if (!isRefresh) setLoading(true);
      setError(null);
      try {
        const result = await getMyWeeklyAttendance({
          weekStart: week.weekStart,
          weekEnd: week.weekEnd,
        });
        setReport(result.items[0] ?? null);
      } catch (caught) {
        setError(
          attendanceErrorMessage(caught, 'Unable to load your weekly attendance.'),
        );
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [week.weekEnd, week.weekStart],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PermissionGuard
      permission={PERMISSIONS.ATTENDANCE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen
        scroll
        webLayout="wide"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              void load(true);
            }}
            tintColor={palette.primary}
          />
        }
      >
        <ScreenHeader
          title="My Weekly Attendance"
          onBack={canGoBack ? () => navigation.goBack() : undefined}
        />
        <View
          style={[
            styles.weekBar,
            { backgroundColor: palette.surface, borderColor: palette.border },
          ]}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Previous week"
            onPress={() => setWeek(current => shiftIsoWeek(current.weekStart, -1))}
          >
            <Text style={[styles.chevron, { color: palette.primary }]}>‹</Text>
          </Pressable>
          <Text style={[styles.weekLabel, { color: palette.textPrimary }]}>
            {formatWeekRangeLabel(week.weekStart, week.weekEnd)}
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Next week"
            onPress={() => setWeek(current => shiftIsoWeek(current.weekStart, 1))}
          >
            <Text style={[styles.chevron, { color: palette.primary }]}>›</Text>
          </Pressable>
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="This week"
          onPress={() => setWeek(getIsoWeekBounds())}
        >
          <Text style={[styles.thisWeek, { color: palette.primary }]}>This week</Text>
        </Pressable>
        {loading ? (
          <AppLoader message="Loading weekly attendance" />
        ) : error ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : !report ? (
          <EmptyState
            title="No data available"
            message="No attendance was found for this week."
          />
        ) : (
          <WeeklyAttendanceReportCard report={report} />
        )}
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  weekBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  weekLabel: { ...typography.body, fontWeight: '700', flex: 1, textAlign: 'center' },
  chevron: { fontSize: 28, fontWeight: '600', paddingHorizontal: spacing.sm },
  thisWeek: {
    ...typography.caption,
    fontWeight: '700',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
});
